import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import dayjs from 'dayjs';
import { FC, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';
import Divider from 'primitives/Divider';

import { FormInput, FormPanel } from 'components/FormPanel';

import countries from 'utils/countries.json';
import {
  AUSTRALIAN_STATES,
  CANADIAN_STATES,
  GERMAN_STATES,
  INDIAN_STATES,
  SINGAPORE_STATES,
  UAE_STATES,
  UNITED_KINGDOM_STATES,
  US_STATES,
} from 'utils/states';

const UPSERT_KYC_MUTATION = gql(`
  mutation UpsertKyc(
    $investmentEntityId: ID!
    $pan: String!
    $identityProofDocument: Upload
    $addressProofDocument: Upload
    $dateOfBirth: String!
    $lineOne: String!
    $lineTwo: String
    $city: String!
    $pincode: String!
    $state: String!
    $country: String!
  ) {
    upsertKyc(
      investmentEntityId: $investmentEntityId
      pan: $pan
      identityProofDocument: $identityProofDocument
      addressProofDocument: $addressProofDocument
      dateOfBirth: $dateOfBirth
      lineOne: $lineOne
      lineTwo: $lineTwo
      city: $city
      pincode: $pincode
      state: $state
      country: $country
    ) {
      id
      kyc {
        id
        pan
        identityProofDocument {
          url
          name
        }
        addressProofDocument {
          url
          name
        }
        dateOfBirth
        address {
          id
          lineOne
          lineTwo
          city
          state
          pincode
          country
        }
      }
    }
  }
`);

const UpsertKyc: FC<{
  investmentEntityId: string;
}> = ({ investmentEntityId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const [upsertKyc, { loading, error }] = useMutation(UPSERT_KYC_MUTATION);

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Add KYC</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel>
          <DialogTitle>Add KYC Information</DialogTitle>
          <DialogSubTitle>Add KYC Information</DialogSubTitle>
          <FormPanel
            loading={loading}
            error={error}
            onSubmit={data => {
              upsertKyc({
                variables: {
                  investmentEntityId,
                  pan: data.pan,
                  identityProofDocument: data.identityProofDocument[0],
                  addressProofDocument: data.addressProofDocument[0],
                  dateOfBirth: data.dateOfBirth,
                  lineOne: data.lineOne,
                  lineTwo: data.lineTwo,
                  city: data.city,
                  state: data[data.country],
                  pincode: data.pincode,
                  country: data.country,
                },
              });
            }}
          >
            <FormInput
              fieldName="pan"
              type="text"
              fullWidth
              defaultValue={''}
              label="PAN"
              validators={{
                required: true,
                isPAN: true,
              }}
              span="3"
            />
            <FormInput
              fieldName="dateOfBirth"
              type="date"
              fullWidth
              defaultValue={''}
              label="Date of Birth"
              maxDate={dayjs()}
              validators={{
                required: true,
              }}
              span="3"
            />
            <Divider className="col-span-full" />
            <FormInput
              fieldName="lineOne"
              type="text"
              fullWidth
              defaultValue={''}
              label="Line One"
              validators={{
                required: true,
              }}
            />
            <FormInput
              fieldName="lineTwo"
              type="text"
              fullWidth
              defaultValue={''}
              label="Line Two"
            />
            <FormInput
              fieldName="city"
              type="text"
              fullWidth
              defaultValue={''}
              label="City"
              validators={{
                required: true,
              }}
            />
            <FormInput
              fieldName="IN"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(INDIAN_STATES).map(state => ({
                label: INDIAN_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'IN' }],
              }}
            />
            <FormInput
              fieldName="US"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(US_STATES).map(state => ({
                label: US_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'US' }],
              }}
            />
            <FormInput
              fieldName="GE"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(GERMAN_STATES).map(state => ({
                label: GERMAN_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'GE' }],
              }}
            />
            <FormInput
              fieldName="SG"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(SINGAPORE_STATES).map(state => ({
                label: SINGAPORE_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'SG' }],
              }}
            />
            <FormInput
              fieldName="UK"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(UNITED_KINGDOM_STATES).map(state => ({
                label: UNITED_KINGDOM_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'UK' }],
              }}
            />
            <FormInput
              fieldName="AE"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(UAE_STATES).map(state => ({
                label: UAE_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'AE' }],
              }}
            />
            <FormInput
              fieldName="AU"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(AUSTRALIAN_STATES).map(state => ({
                label: AUSTRALIAN_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'AU' }],
              }}
            />
            <FormInput
              fieldName="CA"
              type="select"
              fullWidth
              defaultValue={''}
              label="State"
              validators={{ required: true }}
              options={Object.keys(CANADIAN_STATES).map(state => ({
                label: CANADIAN_STATES[state],
                value: state,
              }))}
              conditionsToShow={{
                matches: [{ field: 'country', condition: '===', value: 'CA' }],
              }}
            />
            <FormInput
              fieldName="pincode"
              type="text"
              fullWidth
              defaultValue={''}
              label="Pincode"
              validators={{
                required: true,
              }}
            />
            <FormInput
              fieldName="country"
              type="select"
              fullWidth
              defaultValue={''}
              label="Country"
              options={Object.keys(countries).map(country => ({
                label: countries[country],
                value: country,
              }))}
              validators={{
                required: true,
              }}
            />
            <Divider className="col-span-full" />
            <FormInput
              type="upload"
              fieldName="identityProofDocument"
              label="Upload Aadhaar Card"
              defaultValue={''}
              validators={{
                required: true,
              }}
              span="3"
            />
            <FormInput
              type="upload"
              fieldName="addressProofDocument"
              label="Upload PAN Card"
              defaultValue={''}
              validators={{
                required: true,
              }}
              span="3"
            />
          </FormPanel>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default UpsertKyc;
